import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewRef } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { IResponseLower, PrebootService, SeoService, UiHelperService } from 'core-max-lib';
import { Subscription } from 'rxjs';
import { ILcImage, MagazineService } from '../../interfaces/magazine-service.interface';
import { AdditionalInfoContainerResult, AdditionalInfoResult } from '../../models/additional-info-result';
import { IAdditionalInfoContainerResult, IMagazineArticle, IMagazineCategoryArticleResult, IOrderedItem, IQuestionsAndAnswersContainerResult } from '../../models/magazine-result';

declare var $: any;

@Component({
    selector: 'lib-magazine-article',
    templateUrl: './magazine-article.component.html',
    styleUrls: ['./magazine-article.component.scss']
})
export class MagazineArticleComponent
    implements OnInit, OnDestroy, AfterViewInit {
    constructor(
        @Inject(DOCUMENT) private readonly document: any,
        private uiHelper: UiHelperService,
        public sanitizer: DomSanitizer,
        public magazineService: MagazineService,
        public router: Router,
        private activatedRoute: ActivatedRoute,
        private prebootSvc: PrebootService,
        private seoService: SeoService
    ) { }


    @ViewChild('moreInfoSection')
    moreInfoSection: ElementRef;
    @ViewChild('moreInfoVC', { read: ViewContainerRef })
    moreInfoVC: ViewContainerRef;
    @ViewChild('moreInfoT', { read: TemplateRef })
    moreInfoT: TemplateRef<any>;
    moreInfoViewRef: ViewRef;

    subs: Subscription[] = [];
    public text: SafeHtml;
    public title: string;
    public subtitle: string;
    public date: string;
    public extraInfo: string;
    public desktopImg: ILcImage;
    public mobileImg: ILcImage;
    public faq: IQuestionsAndAnswersContainerResult;
    public safeMailToUrl: SafeUrl;
    public interestingArticles: Array<IMagazineArticle> = [];
    public interestingArticlesContainer: IAdditionalInfoContainerResult;
    public magazineCategory: IMagazineCategoryArticleResult;
    public cta: boolean;
    public ctaText: string;
    public ctaUrl: string;
    public ctaTarget: string;
    public ctaBackgroundColor: string;
    public ctaTextColor: string;
    public playbuzz: boolean;
    public playbuzzDiv: SafeHtml;
    public showImageInArticlePage: boolean;
    public textAndVideos: IOrderedItem<any>[];
    public disclaimerText: string;

    isMobile = false;
    noMainPicture = false;
    lastScrollRatio: number = 0;

    ngAfterViewInit() {
        if (this.playbuzz && this.playbuzzDiv) {
            this.loadPlaybuzzScript();
        }
    }

    ngOnInit() {
        this.isMobile = this.magazineService.isMobile();
        this.magazineService.insertStylesFix();

        this.subs.push(this.activatedRoute.params.subscribe((params: Params) => {
            const requestedArticle = params['magazine-article'];
            const requestedCategory = params['magazine-category'];

            this.subs.push(this.magazineService.getMagazineArticle(requestedArticle, requestedCategory)
                .subscribe((res: IResponseLower<IMagazineCategoryArticleResult>) => {
                    if (res.returnCode !== 0) {
                        this.magazineService.redirectToErrorPage();
                        return;
                    }

                    let article: IMagazineArticle;
                    let activeCategory = res.result;

                    article = res.result.article;
                    if (!article) {
                        article = activeCategory.articles.filter(a =>
                            a.title.toLowerCase() === params['magazine-article'].toLowerCase())[0];
                    }

                    if (!article) {
                        this.magazineService.redirectToErrorPage();
                        return;
                    }
                    this.magazineCategory = activeCategory;


                    console.log("this.magazineCategory.name: " + this.magazineCategory.name );
                    this.faq = article.questionsAndAnswersContainer;
                    if (this.faq && this.faq.questionsAndAnswers) {
                        const faqCore = this.faq.questionsAndAnswers.slice(0, 5).map(qa => {return {QuestionText: qa.questionText, AnswerText: qa.answerText}});
                        this.seoService.setQuestionAndAnswers(faqCore);
                    }
                    this.title = article.title;
                    this.subtitle = article.subtitle;
                    if (article.text) {
                        this.text = this.sanitizer.bypassSecurityTrustHtml(article.text);
                    }
                    this.showImageInArticlePage = article.showImageInArticlePage;
                    this.desktopImg = article.desktopImage;
                    this.mobileImg = article.mobileImage;
                    this.noMainPicture = !this.showImageInArticlePage || !this.mobileImg.url || !this.desktopImg.url;
                    this.date = article.date;
                    this.extraInfo = article.extraInfo;
                    this.cta = article.cta;
                    this.ctaText = article.ctaText;
                    this.ctaUrl = article.ctaUrl;
                    this.ctaTarget = article.ctaTarget;
                    this.ctaBackgroundColor = article.ctaBackgroundColor;
                    this.ctaTextColor = article.ctaTextColor;
                    this.textAndVideos = article.textAndVideos;
                    if (this.textAndVideos && this.textAndVideos.length) {
                        this.textAndVideos.forEach(tav => {
                            if (tav.itemName === 'video' && tav.item.text && this.uiHelper.isBrowser) {
                                tav.item.text = this.sanitizer.bypassSecurityTrustHtml(tav.item.text.changingThisBreaksApplicationSecurity || tav.item.text);
                            }
                        });
                    }
                    this.disclaimerText = article.disclaimerText;
                    if (article.playbuzz && article.playbuzzDiv) {
                        this.playbuzz = article.playbuzz;
                        this.playbuzzDiv = this.sanitizer.bypassSecurityTrustHtml(article.playbuzzDiv);
                        if (this.uiHelper.isBrowser) {
                            const playbuzz = (window as any).Playbuzz;
                            if (playbuzz) {
                                playbuzz.render();
                            }
                        }

                        this.prebootSvc.transitionComplete.subscribe(stable => {
                            if (stable) {
                                this.loadPlaybuzzScript();
                            }
                        });
                    }
                    this.safeMailToUrl = this.generateSafeMailToUrl(
                        this.title,
                        window.location.href
                    );

                    this.pushToGtmArticle('Load');

                    // more-articles
                    this.interestingArticles = [];
                    if (activeCategory.articles.length < 4) {
                        return;
                    }
                    const usedIndexes = [];
                    for (
                        let i = this.getRandomInt(
                            activeCategory.articles.length
                        );
                        this.interestingArticles.length < 3;
                        i = this.getRandomInt(activeCategory.articles.length)
                    ) {
                        if (
                            activeCategory.articles[i].pageUrl.toLowerCase() !==
                            params['magazine-article'].toLowerCase() &&
                            activeCategory.articles[i].title !==
                            params['magazine-article'] &&
                            usedIndexes.indexOf(i) < 0 // we haven't used this index yet
                        ) {
                            this.interestingArticles.push(
                                activeCategory.articles[i]
                            );

                            usedIndexes.push(i);
                        }
                    }

                    if (this.interestingArticles.length === 3) {
                        this.interestingArticlesContainer = new AdditionalInfoContainerResult();
                        this.interestingArticlesContainer.additionalInfoMainTitle =
                            res.result.moreInfoTitle;
                        this.interestingArticlesContainer.additionalInfoLinkText = res
                            .result.moreInfoLinkText
                            ? res.result.moreInfoLinkText
                            : 'לכל הכתבות';
                        this.interestingArticlesContainer.additionalInfoLinkUrl =
                            res.result.moreInfoLinkUrl;
                        this.interestingArticlesContainer.additionalInfoCategoryName =
                            activeCategory.name
                        this.interestingArticlesContainer.additionalInfoList = [];
                        for (let i = 0; i < 3; i++) {
                            const interestingArticle = this.interestingArticles[
                                i
                            ];
                            const additionalInfoArticle = new AdditionalInfoResult();
                            additionalInfoArticle.additionalInfoTitle =
                                interestingArticle.title;
                            additionalInfoArticle.additionalInfoText =
                                interestingArticle.subtitle;
                            additionalInfoArticle.additionalInfoLinkUrl =
                                interestingArticle.pageUrl == null
                                    ? this.router
                                        .createUrlTree([
                                            activeCategory.urlName,
                                            'magazine',
                                            interestingArticle.title
                                        ])
                                        .toString()
                                    : this.router
                                        .createUrlTree([
                                            activeCategory.urlName,
                                            'magazine',
                                            interestingArticle.pageUrl
                                        ])
                                        .toString();
                            additionalInfoArticle.additionalInfoLinkText = interestingArticle.moreInfoReadMoreText
                                ? interestingArticle.moreInfoReadMoreText
                                : 'קרא עוד';
                            additionalInfoArticle.additionalInfoImage =
                                interestingArticle.moreInfoImage;

                            this.interestingArticlesContainer.additionalInfoList.push(
                                additionalInfoArticle
                            );
                        }
                    } else {
                        this.magazineService.redirectToErrorPage();
                        return;
                    }
                }));
        }));

        this.createRteImgStyle();
    }

    generateSafeMailToUrl(subject: string, body: string): SafeUrl {
        let href = 'javascript:location.href = ';
        let url: string = 'mailto:?to=&subject=' + subject + '&body=' + body;
        url = encodeURI(url);
        url = `"` + url + `"`;
        href += url;
        return this.sanitizer.bypassSecurityTrustUrl(href);
    }

    ngOnDestroy(): void {
        this.magazineService.removeStylesFix();
        this.subs.forEach(sub => sub.unsubscribe())
    }

    getRandomInt(max) {
        return Math.floor(Math.random() * Math.floor(max));
    }

    navigateToHomePage() {
        return this.router.navigate(['/']);
    }

    navigateToMagazinePage(e) {
        e.preventDefault();
        return this.router.navigate([
            this.magazineCategory.urlName,
            'magazine'
        ]);
    }

    navigateToProductPage(e) {
        e.preventDefault();
        return this.router.navigate([this.magazineCategory.productPageUrl]);
    }

    navigateToMagazineArticle(article: IMagazineArticle) {
        return article.pageUrl == null
            ? this.router.navigate(['magazine', article.title])
            : this.router.navigate(['magazine', article.pageUrl]);
    }

    private loadScript(html: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.innerHTML = html;

            script.onload = () => {
                resolve(true);
            };

            script.onerror = () => {
                reject(false);
            };

            document.head.appendChild(script);
        });
    }

    async loadPlaybuzzScript(): Promise<any> {
        const loads = [];
        loads.push(
            this.loadScript(
                '(function(d, s, id) {var js,fjs = d.getElementsByTagName(s)[0];if (d.getElementById(id)) return;js = d.createElement(s);js.id = id;js.src = \'https://embed.ex.co/sdk.js\';fjs.parentNode.insertBefore(js, fjs);})(document, \'script\', \'playbuzz-sdk\');'
            )
        );

        if (loads.length) {
            await Promise.all(loads);

        }
    }

    createRteImgStyle() {
        if (
            this.document.head.querySelector(`style[id="magazineRteImgStyle"]`)
        ) {
            return;
        }

        const styleElement = this.document.head.appendChild(
            this.document.createElement('style')
        ) as HTMLStyleElement;

        styleElement.setAttribute('type', 'text/css');
        styleElement.setAttribute('id', 'magazineRteImgStyle');
        styleElement.innerHTML = `@media (max-width: 575px) {
            .magazine .description-wrapper .description p img {
                max-width: 100%;
                width: auto !important;
                height: auto !important;
            }
        }
        .magazine .description-wrapper .description p ol li { list-style-type: decimal; }
        .magazine .description-wrapper .description p ul li { list-style-type: disc;}
        .magazine .description-wrapper .description table tbody { border: none; }`;
    }

    @HostListener('window:scroll', ['$event']) // for window scroll events
    onScroll(event) {
        let pos = event.target.scrollingElement.clientHeight + event.target.scrollingElement.scrollTop;
        let max = event.target.scrollingElement.scrollHeight;
        let ratio = pos / max * 100 | 0;
        if (ratio !== this.lastScrollRatio && [25, 50, 75, 100].includes(ratio)) {
            this.pushToGtmArticle('Scroll - ' + ratio)
            this.lastScrollRatio = ratio;
        }
    }

    pushToGtmArticle(label: string) {
        this.magazineService.pushToGtm('article – ' + this.magazineCategory?.name + ' - ' + this.title, label);
    }
}
